import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { ILoginParams } from '../models/login-params.interface';
import { MFACodeDTO } from '../models/mfaCode.dto';
import { PermissionEnum } from '../models/permission.enum';
import { ResponseStatus } from '../models/response-status.interface';
import { ISession } from '../models/session.interface';
import { TableService } from '../modules/table/table.service';
import { ChangePasswordDTO } from '../pages/profile/models/change-password.dto';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public authUser = new BehaviorSubject<ISession | undefined>(undefined);

  constructor(
    private router: Router,
    private http: HttpClient,
    private ngxPermissionsService: NgxPermissionsService,
  ) {}

  public login(params: ILoginParams): Observable<ISession> {
    return this.http
      .post<ISession>(`${environment.apiPrefix}/admin/login`, params, {
        observe: 'response',
      })
      .pipe(
        map((r) => {
          const u = r.body || ({} as ISession);
          const isMFAEnabled = r.headers?.get('is-mfa-required');
          if (isMFAEnabled) {
            u.mfaRequired = true;
          }

          this.authUser.next(u);

          const permissionsNames = u.permissions.map((p) => PermissionEnum[p]);
          this.ngxPermissionsService.loadPermissions(permissionsNames);
          return u;
        }),
      );
  }

  public changePassword(params: ChangePasswordDTO): Observable<void> {
    return this.http.post<void>(
      `${environment.apiPrefix}/admin/profile/change-password`,
      params,
    );
  }

  public async logout() {
    await firstValueFrom(
      this.http.get(`${environment.apiPrefix}/admin/logout`),
    );

    for (const key in localStorage) {
      if (key.startsWith(TableService.settingsPrefix)) {
        continue;
      }

      localStorage.removeItem(key);
    }

    await this.router.navigate(['/login']);
    location.reload();
  }

  public loadSession(): Observable<ISession> {
    return this.http
      .get<ISession>(`${environment.apiPrefix}/admin/session`)
      .pipe(tap((u) => this.authUser.next(u)));
  }

  public turnOnMFA(body: MFACodeDTO): Observable<ResponseStatus> {
    return this.http.post<ResponseStatus>(
      `${environment.apiPrefix}/admin/mfa/turn-on`,
      body,
    );
  }

  public authenticateMFA(body: MFACodeDTO): Observable<ResponseStatus> {
    return this.http.post<ResponseStatus>(
      `${environment.apiPrefix}/admin/mfa/authenticate`,
      body,
    );
  }
}
